import { computed } from 'vue'
import { useData } from '@bagelink/blox'
import { ensureAuthors, resolveAuthorForBlock } from './useDatastore'
import { normalizeParashaName, useWeeklyParasha } from './useWeeklyParasha'
import { isPublicRecord } from '../utils/publication'

/**
 * Homepage widget data.
 *
 * The homepage widget is a *weekly parashah* widget: its three tabs describe the
 * Torah portion read on the coming Shabbat, exactly like the legacy site:
 *
 * - **Latest**   – newest pieces tagged with the current parashah (`parshaIds`)
 * - **Popular**  – most-read pieces of that parashah (`hits`)
 * - **Featured** – the parashah's editor-curated `featuredArticleIds`
 *
 * The datastore API cannot filter on an array field (`parshaIds contains …` is
 * rejected), so the parashah scoping is computed from a light projection of the
 * whole article collection (id/slug/title/image/date/hits/parshaIds/authors-only,
 * ~0.25 MB gzipped) that is fetched once per week and cached by `useData`. The
 * prerendered homepage embeds only the selected cards in the hydration payload,
 * so a first visit never downloads the projection.
 */

const STORE = 'the_torah'
const SSR_API_ORIGIN = 'https://torah.bagel.to'

// Only the fields the widget needs. The datastore returns `_id`/`id` regardless.
const ARTICLE_FIELDS = [
	'_id',
	'slug',
	'title',
	'image',
	'publishDate',
	'hits',
	'parshaIds',
	'authorIds',
	'authorId',
	'secondAuthorId',
	'thirdAuthorId',
	'featuredHomepage',
]
const PARASHAH_FIELDS = ['_id', 'name', 'slug', 'featuredArticleIds']

type Row = Record<string, any>

export interface ParashaWidgetCard {
	title: string
	authors: { name: string, title: string, image: string }[]
	image: string
	category: string
	link: string
}

interface ParashaWidgetSelection {
	parashah: string
	latest: Row[]
	popular: Row[]
	featured: Row[]
}

const EMPTY_SELECTION: ParashaWidgetSelection = {
	parashah: '',
	latest: [],
	popular: [],
	featured: [],
}

/**
 * The SSG runtime polyfills `window`, so `typeof window` cannot tell the prerender
 * apart from the browser. Node is the only place `process.versions.node` exists.
 */
function isNodeRuntime(): boolean {
	return typeof process !== 'undefined' && !!process.versions?.node
}

/**
 * Browser requests go through the same-origin `/api` proxy (Vite dev proxy,
 * Netlify `_redirects`), which strips the prefix and hits the public datastore.
 * The prerender has no proxy, so it needs the absolute API host.
 */
function datastoreUrl(collection: string, params: URLSearchParams): string {
	const path = `/datastore/${STORE}/collections/${collection}?${params}`
	if (!isNodeRuntime()) return `/api${path}`
	const env = (globalThis as any)?.process?.env?.BAGELINK_API_URL
	const host = (typeof env === 'string' && env ? env : SSR_API_ORIGIN).replace(/\/+$/, '')
	return `${host}${path}`
}

async function fetchCollection(collection: string, fields: string[]): Promise<Row[]> {
	const rows: Row[] = []
	const limit = 1000
	for (let offset = 0; ; offset += limit) {
		const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
		// `select` keeps the payload to the fields the widget reads (~150 KB gzipped
		// instead of the whole article bodies). `_id` is required by `isPublicRecord`.
		for (const field of fields) params.append('select', field)
		const response = await fetch(datastoreUrl(collection, params), {
			headers: { accept: 'application/json' },
		})
		if (!response.ok) throw new Error(`Datastore ${collection}: HTTP ${response.status}`)
		const page = await response.json()
		const batch: Row[] = Array.isArray(page) ? page : page?.items ?? []
		// Filter before deriving anything from the page (unknown ids stay private).
		rows.push(...batch.filter(row => isPublicRecord(collection, row)))
		if (batch.length < limit) break
	}
	return rows
}

/** Datastore values are usually arrays, but the `locale` serializer stringifies them. */
function asIdList(value: unknown): string[] {
	if (Array.isArray(value)) return value.filter((id): id is string => typeof id === 'string' && !!id)
	if (typeof value !== 'string' || !value.trim()) return []
	try {
		const parsed = JSON.parse(value.replace(/'/g, '"'))
		if (Array.isArray(parsed)) return parsed.filter((id): id is string => typeof id === 'string' && !!id)
	} catch {
		/* not an encoded list */
	}
	return []
}

/** Booleans arrive as `true`/`false`, or as `"True"`/`"False"` strings. */
function isFlag(value: unknown): boolean {
	if (typeof value === 'boolean') return value
	if (typeof value === 'number') return value !== 0
	if (typeof value === 'string') return ['true', '1', 'yes'].includes(value.trim().toLowerCase())
	return false
}

function hitCount(row: Row): number {
	const hits = Number(row.hits)
	return Number.isFinite(hits) ? hits : 0
}

function byNewest(a: Row, b: Row): number {
	return new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime()
}

function uniqueById(rows: Row[]): Row[] {
	const seen = new Set<string>()
	return rows.filter((row) => {
		const id = String(row._id ?? row.id ?? '')
		if (!id || seen.has(id)) return false
		seen.add(id)
		return true
	})
}

async function selectWidgetArticles(portionNames: string[]): Promise<ParashaWidgetSelection> {
	const [parashahs, articles] = await Promise.all([
		fetchCollection('parashahs', PARASHAH_FIELDS),
		fetchCollection('articles', ARTICLE_FIELDS),
	])

	const wanted = new Set(portionNames.map(normalizeParashaName).filter(Boolean))
	const parashahsNow = wanted.size
		? parashahs.filter(p => wanted.has(normalizeParashaName(String(p.name ?? ''))))
		: []

	const parashahIds = new Set(
		parashahsNow
			.flatMap(p => [p._id, p.id])
			.filter((id): id is string => typeof id === 'string' && !!id),
	)

	// No portion (festival Shabbat) ⇒ fall back to the whole collection.
	const pool = parashahIds.size
		? articles.filter(article => asIdList(article.parshaIds).some(id => parashahIds.has(id)))
		: articles

	const latest = uniqueById([...pool].sort(byNewest)).slice(0, 2)
	const latestIds = new Set(latest.map(article => String(article._id ?? '')))
	const popular = uniqueById([...pool].sort((a, b) => hitCount(b) - hitCount(a))).slice(0, 2)

	// Featured is editor-curated per parashah — never "the newest pieces", which is
	// exactly what made the Latest and Featured tabs show the same articles.
	const byId = new Map<string, Row>()
	for (const article of articles) {
		if (typeof article._id === 'string') byId.set(article._id, article)
	}
	const curated = uniqueById(
		parashahsNow
			.flatMap(parashah => asIdList(parashah.featuredArticleIds))
			.map(id => byId.get(id))
			.filter((article): article is Row => !!article),
	)
	const flagged = uniqueById(pool.filter(article => isFlag(article.featuredHomepage)))
	const featured = curated.length
		? curated
		// Fallbacks: an explicitly flagged piece, then the next-newest that the
		// Latest tab isn't already showing.
		: flagged.length
			? flagged
			: uniqueById([...pool].sort(byNewest)).filter(article => !latestIds.has(String(article._id ?? '')))

	return {
		parashah: parashahsNow.length ? String(parashahsNow[0].name ?? '') : '',
		latest,
		popular,
		featured: featured.slice(0, 2),
	}
}

function toCard(article: Row): ParashaWidgetCard {
	const authorIds = asIdList(article.authorIds)
	const ids = authorIds.length
		? authorIds
		: [article.authorId, article.secondAuthorId, article.thirdAuthorId].filter(Boolean)
	const authors = ids.map(resolveAuthorForBlock).filter(Boolean)
	return {
		title: article.title || '',
		authors: authors.length ? authors : article.authors || [],
		image: article.image || '',
		category: article.category || '',
		link: article.link || `/article/${article.slug ?? ''}`,
	}
}

/**
 * Per-tab articles for the homepage widget, scoped to the current week's parashah.
 */
export function useParashaWidget() {
	ensureAuthors()
	const { portions } = useWeeklyParasha()
	const portionNames = computed(() => portions.value.map(portion => portion.name))
	const key = computed(() => `parashah-widget:${portionNames.value.join('|') || 'all'}`)

	const { data, pending } = useData<ParashaWidgetSelection>(
		key,
		() => selectWidgetArticles(portionNames.value),
		{ default: EMPTY_SELECTION },
	)

	return {
		loading: computed(() => pending.value),
		hasArticles: computed(() =>
			data.value.latest.length > 0
			|| data.value.popular.length > 0
			|| data.value.featured.length > 0,
		),
		latest: computed(() => data.value.latest.map(toCard)),
		popular: computed(() => data.value.popular.map(toCard)),
		featured: computed(() => data.value.featured.map(toCard)),
	}
}
